// Single source of truth for reading/writing the site's cookie-consent
// decision (`effortless_consent` in localStorage), shared by ConsentBanner
// and anything else that needs to know the visitor's current consent state
// (e.g. marketing attribution capture). Centralizing this avoids the two
// call sites drifting - e.g. one applying the 180-day expiry and the other
// treating any parseable stored value as current forever.

export interface ConsentPreferences {
  analytics: boolean;
  ads: boolean;
  timestamp: string;
}

export const CONSENT_KEY = "effortless_consent";
export const CONSENT_EXPIRY_DAYS = 180;

/** Reads the stored consent decision, or null if absent/malformed/expired (clearing it from storage when expired). */
export function getStoredConsent(): ConsentPreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored) as ConsentPreferences;
    const storedDate = new Date(consent.timestamp);
    const now = new Date();
    const daysDiff = (now.getTime() - storedDate.getTime()) / (1000 * 60 * 60 * 24);

    if (Number.isNaN(daysDiff) || daysDiff > CONSENT_EXPIRY_DAYS) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }

    return consent;
  } catch {
    return null;
  }
}

export function saveConsent(preferences: Omit<ConsentPreferences, "timestamp">) {
  const consent: ConsentPreferences = {
    ...preferences,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  // Same-tab listeners (e.g. a sticky mobile CTA that must not overlap the
  // cookie banner) can't rely on the storage event, which only fires in
  // other tabs - so broadcast the change locally too.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("consent-changed"));
  }
}
